import { brandChatbotSettingEnabled } from "@faire/help-center-utils/lib/brandChatbotSettingEnabled";
import { retailerChatbotSettingEnabled } from "@faire/help-center-utils/lib/retailerChatbotSettingEnabled";
import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { browserLevelLanguage } from "@faire/help-center-utils/serialized-data/getLocale";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { getIsLoggedInUser } from "@faire/help-center-utils/serialized-data/getUser";
import { isProduction } from "@faire/web--source/common/user-agent/isProduction";
import { useQuery as useAIChatbotAccessQuery } from "@faire/web-api--source/api/help-center/access-control/get-hooks";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";

interface IAIChatbotAccessResponse {
  hasAIChatbotAccess: boolean;
  isAccessControlLoading: boolean;
}

export const useAIChatbotAccess = (): IAIChatbotAccessResponse => {
  const isLoggedInUser = getIsLoggedInUser();
  const lang = browserLevelLanguage();
  const engOnly = lang === Language.ENG;
  const retailerToken = getRetailerToken();
  const brandToken = getBrandToken();
  const shouldEnableAccessControlQuery = isProduction()
    ? isLoggedInUser && engOnly
    : isLoggedInUser;

  const {
    data: accessControlResponse,
    status: accessControlStatus,
    isLoading: isAccessControlLoading,
  } = useAIChatbotAccessQuery({
    enabled: shouldEnableAccessControlQuery,
  });

  const authorizedCustomer = !!(
    accessControlResponse?.has_chatbot && accessControlStatus === "success"
  );

  if (!authorizedCustomer) {
    return {
      hasAIChatbotAccess: false,
      isAccessControlLoading: false,
    };
  }

  // If it's a retailer, we need to check the retailer chatbot setting.
  if (retailerToken) {
    return {
      hasAIChatbotAccess: retailerChatbotSettingEnabled(),
      isAccessControlLoading,
    };
  }

  // If it's a brand, we need to check the brand chatbot setting.
  if (brandToken) {
    return {
      hasAIChatbotAccess: brandChatbotSettingEnabled(),
      isAccessControlLoading,
    };
  }

  return {
    hasAIChatbotAccess: false,
    isAccessControlLoading: false,
  };
};
